import type { FC } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography } from '@mui/material'
import type {
  ClaudeSession,
  Command,
  UserInputDraft,
} from '@/features/events/conversation/state/types'
import { getLatestConversationTurn } from '@/features/events/conversation/state/types'
import type { UUID } from '@/common/types'
import {
  DRAFT_DIALOG_CONFIG,
  UserAnswerDraftItem,
  UserInputDraftItem,
} from '@/features/events/pages/shared'
import { useCommandSubmitter } from '@/features/events/conversation/controls'
import {
  useSubmitUserInputDraftHandler,
  useUserAnswerDraftEntries,
} from '@/features/events/conversation/drafts'
import { resolveSessionAgentMode } from '@/features/events/shared/utils/agentModeUtils'
import { SessionDialog } from './SessionDialog'
import { UserInputDialog } from './UserInputDialog'

interface SessionDraftsDialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly session: ClaudeSession
  readonly recordCommand: (sessionId: UUID, userPromptId: UUID, userCommand: Command) => void
  readonly createNewTurn: (session: ClaudeSession, userPromptId: UUID, userCommand: Command) => void
}

export const SessionDraftsDialog: FC<SessionDraftsDialogProps> = ({
  open,
  onClose,
  session,
  recordCommand,
  createNewTurn,
}) => {
  const [selectedDraft, setSelectedDraft] = useState<UserInputDraft | null>(null)
  const [selectedQuestionHeader, setSelectedQuestionHeader] = useState<string | null>(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [answerEditDialogOpen, setAnswerEditDialogOpen] = useState(false)

  const userInputDrafts = session.runtime.memory.userInputDrafts
  const userAnswersDraft = session.runtime.memory.userAnswersDraft
  const userAnswerDraftEntries = useUserAnswerDraftEntries(userAnswersDraft)

  const hasDrafts = userInputDrafts.length > 0 || userAnswerDraftEntries.length > 0

  const conversationTurn = useMemo(() => getLatestConversationTurn(session), [session])

  useEffect(() => {
    if (open && !hasDrafts) {
      onClose()
    }
  }, [open, hasDrafts, onClose])

  const handleSubmitSuccess = useCallback(() => {
    setEditDialogOpen(false)
    setSelectedDraft(null)
  }, [])

  const submitter = useCommandSubmitter({
    session,
    conversationTurn,
    recordCommand,
    createNewTurn,
    onSuccess: handleSubmitSuccess,
  })

  const handleDraftClick = useCallback((draft: UserInputDraft) => {
    setSelectedDraft(draft)
    setEditDialogOpen(true)
  }, [])

  const handleAnswerDraftClick = useCallback((questionHeader: string) => {
    setSelectedQuestionHeader(questionHeader)
    setAnswerEditDialogOpen(true)
  }, [])

  const handleEditClose = useCallback(() => {
    setEditDialogOpen(false)
    setSelectedDraft(null)
  }, [])

  const handleAnswerEditClose = useCallback(() => {
    setAnswerEditDialogOpen(false)
    setSelectedQuestionHeader(null)
  }, [])

  const { handleSubmit } = useSubmitUserInputDraftHandler({
    session,
    selectedDraft,
    submitter,
  })

  const handleAnswerSubmit = useCallback(async (): Promise<boolean> => {
    setAnswerEditDialogOpen(false)
    setSelectedQuestionHeader(null)
    return true
  }, [])

  const dialogConfig = selectedDraft ? DRAFT_DIALOG_CONFIG[selectedDraft.type] : null
  const answerDialogConfig = DRAFT_DIALOG_CONFIG['custom_answer']
  const currentAgentMode = resolveSessionAgentMode(session)

  const selectedAnswerDraftData = useMemo(() => {
    if (!selectedQuestionHeader || !userAnswersDraft) return null
    return userAnswersDraft.answers[selectedQuestionHeader] ?? null
  }, [selectedQuestionHeader, userAnswersDraft])

  return (
    <>
      <SessionDialog
        open={open}
        onClose={onClose}
        title="Session drafts"
        breadcrumb={session.sessionBreadcrumb}
        dialogWidth="600px"
        dialogHeight="auto"
      >
        {hasDrafts ? (
          <Box sx={draftsContainerSx}>
            {userInputDrafts.map((draft) => (
              <UserInputDraftItem
                key={draft.id}
                draft={draft}
                session={session}
                onDraftClick={handleDraftClick}
              />
            ))}
            {userAnswerDraftEntries.length > 0 && (
              <Box sx={answersSectionSx}>
                <Typography sx={answersHeaderSx}>User answers</Typography>
                {userAnswerDraftEntries.map(({ header, answer, index }) => (
                  <UserAnswerDraftItem
                    key={header}
                    questionHeader={header}
                    questionIndex={index}
                    answerDraft={answer}
                    onAnswerDraftClick={handleAnswerDraftClick}
                  />
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Typography color="text.secondary">No drafts</Typography>
        )}
      </SessionDialog>

      {selectedDraft && dialogConfig && (
        <UserInputDialog
          open={editDialogOpen}
          onClose={handleEditClose}
          onSubmit={handleSubmit}
          session={session}
          isSubmitting={submitter.isSubmitting}
          error={submitter.error}
          draftType={selectedDraft.type}
          refEventId={selectedDraft.refEventId}
          title={dialogConfig.title}
          placeholder={dialogConfig.placeholder}
          description={dialogConfig.description}
          submitButtonLabel={dialogConfig.submitButtonLabel}
          submitButtonLoadingLabel={dialogConfig.submitButtonLoadingLabel}
          initialValue={selectedDraft.content}
          currentAgentMode={currentAgentMode}
        />
      )}

      {selectedQuestionHeader && userAnswersDraft && selectedAnswerDraftData && (
        <UserInputDialog
          open={answerEditDialogOpen}
          onClose={handleAnswerEditClose}
          onSubmit={handleAnswerSubmit}
          session={session}
          isSubmitting={false}
          error={null}
          draftType="custom_answer"
          refEventId={null}
          title={answerDialogConfig.title}
          placeholder={answerDialogConfig.placeholder}
          description={answerDialogConfig.description}
          submitButtonLabel={answerDialogConfig.submitButtonLabel}
          submitButtonLoadingLabel={answerDialogConfig.submitButtonLoadingLabel}
          initialValue={selectedAnswerDraftData.customAnswer ?? ''}
          questionHeader={selectedQuestionHeader}
          eventId={userAnswersDraft.eventId}
          deleteOnSubmit={false}
          currentAgentMode={currentAgentMode}
        />
      )}
    </>
  )
}

const draftsContainerSx: SxProps<Theme> = {
  mt: 1.5,
}

const answersSectionSx: SxProps<Theme> = {
  mt: 1.5,
}

const answersHeaderSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'warning.main',
  pl: 2,
  mb: 0.5,
}
